// src/utils/operationCenter/templateUtils.ts
// Dashboard template definitions and utilities

import type {
  UserDashboard,
  DashboardWidget,
  DashboardTemplate,
  LayoutItem,
} from "@/types/dashboard";

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

export function generateId(prefix: string = "id"): string {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

export function now(): string {
  return new Date().toISOString();
}

// ─────────────────────────────────────────────────────────────────────────────
// Template Metadata
// ─────────────────────────────────────────────────────────────────────────────

export interface TemplateMetadata {
  id: DashboardTemplate;
  label: string;
  description: string;
  category: "beginner" | "advanced" | "development";
}

export const TEMPLATE_METADATA: TemplateMetadata[] = [
  {
    id: "trader",
    label: "Trader Dashboard",
    description: "Price action, movers & sentiment",
    category: "beginner",
  },
  {
    id: "cycle_analyst",
    label: "Cycle Analyst",
    description: "On-chain indicators & cycles",
    category: "beginner",
  },
  {
    id: "macro_observer",
    label: "Macro Observer",
    description: "Market indicators & cycles",
    category: "beginner",
  },
  {
    id: "portfolio_manager",
    label: "Portfolio Manager",
    description: "Portfolio tracking & analysis",
    category: "advanced",
  },
  {
    id: "blank",
    label: "Blank Dashboard",
    description: "Start from scratch",
    category: "beginner",
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Template Factories
// ─────────────────────────────────────────────────────────────────────────────

export function createBlankDashboard(name: string): UserDashboard {
  return {
    id: generateId("dash"),
    name,
    layout: [],
    widgets: [],
    created_at: now(),
    updated_at: now(),
  };
}

export function createTraderDashboard(): UserDashboard {
  const id = generateId("dash");
  const widgets: DashboardWidget[] = [
    // Row 1: 4 quarter-width widgets
    {
      id: "w1",
      type: "portfolio_holdings",
      title: "My Holdings",
      config: {},
    },
    {
      id: "w2",
      type: "watchlist",
      title: "Watchlist",
      config: { tickers: ["BTC", "ETH", "SOL", "AVAX", "LINK"] },
    },
    {
      id: "w3",
      type: "top_movers",
      title: "Top Gainers",
      config: { direction: "gainers", time_range: "1d", limit: 10 },
    },
    {
      id: "w4",
      type: "top_movers",
      title: "Top Losers",
      config: { direction: "losers", time_range: "1d", limit: 10 },
    },
    // Row 2: 2 half-width charts
    {
      id: "w5",
      type: "tradingview_chart",
      title: "BTC/USD",
      config: { ticker: "BTC", interval: "D", theme: "dark" },
    },
    {
      id: "w6",
      type: "time_series_chart",
      title: "BTC vs ETH vs SOL",
      config: {
        tickers: ["BTC", "ETH", "SOL"],
        metric: "price_close",
        timeframe: "30d",
        style: "line",
        show_legend: true,
        index_to_100: true,
      },
    },
    // Row 3: 4 quarter-width widgets
    {
      id: "w7",
      type: "tweet_feed",
      title: "Crypto Twitter",
      config: {
        tickers: ["BTC", "ETH", "SOL"],
        sentiment_filter: "all",
        limit: 20,
      },
    },
    {
      id: "w8",
      type: "news_feed",
      title: "News Feed",
      config: { limit: 20 },
    },
    {
      id: "w9",
      type: "fear_greed",
      title: "Fear & Greed",
      config: { show_gauge: true, show_history: false },
    },
    {
      id: "w10",
      type: "daily_recap",
      title: "24h AI Recap",
      config: { tokens: [] },
    },
  ];

  // Standardized grid: w=3 (quarter) or w=6 (half), h=4
  const layout: LayoutItem[] = [
    { i: "w1", x: 0, y: 0, w: 3, h: 4, minW: 3, minH: 4 },
    { i: "w2", x: 3, y: 0, w: 3, h: 4, minW: 3, minH: 4 },
    { i: "w3", x: 6, y: 0, w: 3, h: 4, minW: 3, minH: 4 },
    { i: "w4", x: 9, y: 0, w: 3, h: 4, minW: 3, minH: 4 },
    { i: "w5", x: 0, y: 4, w: 6, h: 4, minW: 6, minH: 4 },
    { i: "w6", x: 6, y: 4, w: 6, h: 4, minW: 6, minH: 4 },
    { i: "w7", x: 0, y: 8, w: 3, h: 4, minW: 3, minH: 4 },
    { i: "w8", x: 3, y: 8, w: 3, h: 4, minW: 3, minH: 4 },
    { i: "w9", x: 6, y: 8, w: 3, h: 4, minW: 3, minH: 4 },
    { i: "w10", x: 9, y: 8, w: 3, h: 4, minW: 3, minH: 4 },
  ];

  return {
    id,
    name: "Trader Dashboard",
    description: "Daily trading overview with movers and sentiment",
    layout,
    widgets,
    created_at: now(),
    updated_at: now(),
  };
}

export function createCycleAnalystDashboard(): UserDashboard {
  const id = generateId("dash");
  const widgets: DashboardWidget[] = [
    // Row 1: 4 quarter-width widgets - Key indicators
    {
      id: "w1",
      type: "market_pulse",
      title: "Market Pulse",
      config: {},
    },
    {
      id: "w2",
      type: "fear_greed",
      title: "Fear & Greed",
      config: { show_gauge: true, show_history: false },
    },
    {
      id: "w3",
      type: "bull_market_indicators",
      title: "Bull Market Signals",
      config: {},
    },
    {
      id: "w4",
      type: "token_metrics_card",
      title: "BTC Metrics",
      config: { ticker: "BTC" },
    },
    // Row 2: 2 half-width cycle indicators
    {
      id: "w5",
      type: "ahr999",
      title: "AHR999 Index",
      config: {},
    },
    {
      id: "w6",
      type: "pi_cycle_top",
      title: "Pi Cycle Top",
      config: {},
    },
    // Row 3: 2 half-width cycle indicators
    {
      id: "w7",
      type: "puell_multiple",
      title: "Puell Multiple",
      config: {},
    },
    {
      id: "w8",
      type: "golden_ratio_multiplier",
      title: "Golden Ratio Multiplier",
      config: {},
    },
    // Row 4: 2 half-width charts
    {
      id: "w9",
      type: "stock_to_flow",
      title: "Stock to Flow",
      config: {},
    },
    {
      id: "w10",
      type: "global_m2",
      title: "Global M2 vs BTC",
      config: {},
    },
  ];

  // Standardized grid: w=3 (quarter) or w=6 (half), h=4
  const layout: LayoutItem[] = [
    { i: "w1", x: 0, y: 0, w: 3, h: 4, minW: 3, minH: 4 },
    { i: "w2", x: 3, y: 0, w: 3, h: 4, minW: 3, minH: 4 },
    { i: "w3", x: 6, y: 0, w: 3, h: 4, minW: 3, minH: 4 },
    { i: "w4", x: 9, y: 0, w: 3, h: 4, minW: 3, minH: 4 },
    { i: "w5", x: 0, y: 4, w: 6, h: 4, minW: 6, minH: 4 },
    { i: "w6", x: 6, y: 4, w: 6, h: 4, minW: 6, minH: 4 },
    { i: "w7", x: 0, y: 8, w: 6, h: 4, minW: 6, minH: 4 },
    { i: "w8", x: 6, y: 8, w: 6, h: 4, minW: 6, minH: 4 },
    { i: "w9", x: 0, y: 12, w: 6, h: 4, minW: 6, minH: 4 },
    { i: "w10", x: 6, y: 12, w: 6, h: 4, minW: 6, minH: 4 },
  ];

  return {
    id,
    name: "Cycle Analyst",
    description: "Bitcoin cycle indicators and on-chain analysis",
    layout,
    widgets,
    created_at: now(),
    updated_at: now(),
  };
}

export function createMacroObserverDashboard(): UserDashboard {
  const id = generateId("dash");
  const widgets: DashboardWidget[] = [
    // Row 1: 4 quarter-width widgets
    {
      id: "w1",
      type: "macro_key_metrics",
      title: "Macro Indicators",
      config: {},
    },
    {
      id: "w2",
      type: "fear_greed",
      title: "Fear & Greed",
      config: { show_gauge: true, show_history: false },
    },
    {
      id: "w3",
      type: "fed_rate_probabilities",
      title: "Fed Rate Probabilities",
      config: {},
    },
    {
      id: "w4",
      type: "economic_calendar",
      title: "Economic Calendar",
      config: {},
    },
    // Row 2: 2 half-width charts
    {
      id: "w5",
      type: "etf_flows",
      title: "BTC ETF Flows",
      config: { ticker: "btc" },
    },
    {
      id: "w6",
      type: "global_m2",
      title: "Global M2 vs BTC",
      config: {},
    },
    // Row 3: 4 quarter-width widgets
    {
      id: "w7",
      type: "fx_movers",
      title: "FX Strongest",
      config: { direction: "strongest" },
    },
    {
      id: "w8",
      type: "fx_movers_weakest",
      title: "FX Weakest",
      config: { direction: "weakest" },
    },
    {
      id: "w9",
      type: "commodities_movers",
      title: "Commodities Gainers",
      config: { direction: "gainers" },
    },
    {
      id: "w10",
      type: "commodities_movers_losers",
      title: "Commodities Losers",
      config: { direction: "losers" },
    },
  ];

  // Standardized grid: w=3 (quarter) or w=6 (half), h=4
  const layout: LayoutItem[] = [
    { i: "w1", x: 0, y: 0, w: 3, h: 4, minW: 3, minH: 4 },
    { i: "w2", x: 3, y: 0, w: 3, h: 4, minW: 3, minH: 4 },
    { i: "w3", x: 6, y: 0, w: 3, h: 4, minW: 3, minH: 4 },
    { i: "w4", x: 9, y: 0, w: 3, h: 4, minW: 3, minH: 4 },
    { i: "w5", x: 0, y: 4, w: 6, h: 4, minW: 6, minH: 4 },
    { i: "w6", x: 6, y: 4, w: 6, h: 4, minW: 6, minH: 4 },
    { i: "w7", x: 0, y: 8, w: 3, h: 4, minW: 3, minH: 4 },
    { i: "w8", x: 3, y: 8, w: 3, h: 4, minW: 3, minH: 4 },
    { i: "w9", x: 6, y: 8, w: 3, h: 4, minW: 3, minH: 4 },
    { i: "w10", x: 9, y: 8, w: 3, h: 4, minW: 3, minH: 4 },
  ];

  return {
    id,
    name: "Macro Observer",
    description: "Market-wide indicators and macro view",
    layout,
    widgets,
    created_at: now(),
    updated_at: now(),
  };
}

export function createPortfolioManagerDashboard(): UserDashboard {
  const id = generateId("dash");
  const widgets: DashboardWidget[] = [
    // Row 1: 4 quarter-width widgets
    {
      id: "w1",
      type: "portfolio_pulse",
      title: "Portfolio Pulse",
      config: { time_range: "7d" },
    },
    {
      id: "w2",
      type: "portfolio_holdings",
      title: "My Holdings",
      config: {},
    },
    {
      id: "w3",
      type: "risk_alerts",
      title: "Threshold Monitor",
      config: {},
    },
    {
      id: "w4",
      type: "fear_greed",
      title: "Market Sentiment",
      config: { show_gauge: true, show_history: false },
    },
    // Row 2: 2 half-width charts
    {
      id: "w5",
      type: "portfolio_nav",
      title: "My Portfolio NAV",
      config: { timeframe: "all", show_benchmark: true },
    },
    {
      id: "w6",
      type: "portfolio_allocation",
      title: "Portfolio Allocation",
      config: {},
    },
    // Row 3: 2 half-width charts
    {
      id: "w7",
      type: "funding_rate",
      title: "Funding Rates",
      config: { tickers: ["BTC"], show_weighted: true },
    },
    {
      id: "w8",
      type: "open_interest",
      title: "Open Interest",
      config: { ticker: "BTC" },
    },
    // Row 4: 4 quarter-width widgets
    {
      id: "w9",
      type: "portfolio_metrics_overview",
      title: "Portfolio Metrics",
      config: {},
    },
    {
      id: "w10",
      type: "correlation_matrix",
      title: "Correlation Matrix",
      config: { metric: "price_close", time_range: "7d" },
    },
  ];

  // Standardized grid: w=3 (quarter) or w=6 (half), h=4
  const layout: LayoutItem[] = [
    { i: "w1", x: 0, y: 0, w: 3, h: 4, minW: 3, minH: 4 },
    { i: "w2", x: 3, y: 0, w: 3, h: 4, minW: 3, minH: 4 },
    { i: "w3", x: 6, y: 0, w: 3, h: 4, minW: 3, minH: 4 },
    { i: "w4", x: 9, y: 0, w: 3, h: 4, minW: 3, minH: 4 },
    { i: "w5", x: 0, y: 4, w: 6, h: 4, minW: 6, minH: 4 },
    { i: "w6", x: 6, y: 4, w: 6, h: 4, minW: 6, minH: 4 },
    { i: "w7", x: 0, y: 8, w: 6, h: 4, minW: 6, minH: 4 },
    { i: "w8", x: 6, y: 8, w: 6, h: 4, minW: 6, minH: 4 },
    { i: "w9", x: 0, y: 12, w: 6, h: 4, minW: 6, minH: 4 },
    { i: "w10", x: 6, y: 12, w: 6, h: 4, minW: 6, minH: 4 },
  ];

  return {
    id,
    name: "Portfolio Manager",
    description: "Portfolio tracking and risk analysis",
    layout,
    widgets,
    created_at: now(),
    updated_at: now(),
  };
}

/**
 * Create a dashboard from a template
 */
export function createDashboardFromTemplate(
  name: string,
  template: DashboardTemplate
): UserDashboard {
  switch (template) {
    case "trader":
      return { ...createTraderDashboard(), name };
    case "cycle_analyst":
      return { ...createCycleAnalystDashboard(), name };
    case "macro_observer":
      return { ...createMacroObserverDashboard(), name };
    case "portfolio_manager":
      return { ...createPortfolioManagerDashboard(), name };
    case "blank":
    default:
      return createBlankDashboard(name);
  }
}
